import type { ComputedRef, Ref } from 'vue';
import {
  computed,
  ref,
  watch,
} from 'vue';

import type {
  IAffiliateProgramCommissionFilter,
  IAffiliateProgramCommissionItem,
  ICommissionCountryFilterItem,
  IConditionVersions,
  ITransactionType,
} from '@modules/affiliate-program-commissions/submodules/edit/types';
import sortByProperty from '@modules/core/utils/sortByProperty';
import { watchImmediate } from '@vueuse/core';

export interface CommissionCountryFilterComposable {
  activeFilterIndex: Ref<number>;
  countryFilters: Ref<ICommissionCountryFilterItem[]>;
  onFilterAdd(): void;
  onFilterSelect(index: number): void;
  onFilterExpandToggle(index: number): void;
  onFilterRemove(index: number): void;
}

export default function useCommissionCountryFilter(
  commission: Ref<IAffiliateProgramCommissionItem | undefined>,
  transactionType: Ref<string | undefined>,
  currentSecondGroupVersion: ComputedRef<IConditionVersions | undefined>,
  secondGroupStepTransactionType: Ref<ITransactionType>,
): CommissionCountryFilterComposable {
  const commissionFilters = computed<IAffiliateProgramCommissionFilter[] | undefined>(() => commission.value?.condition_filters_attributes);
  const activeFilterIndex = ref(0);
  const countryFilters = ref<ICommissionCountryFilterItem[]>([]);
  const maxFilterId = ref(0);

  function onFilterAdd(): void {
    countryFilters.value.push({
      label: undefined,
      emptyLabel: '',
      emptyLabelId: maxFilterId.value + 1,
      value: [],
      expanded: true,
      isAllCountries: false,
      condition_items_attributes: [],
      condition_steps_attributes: [],
    });

    activeFilterIndex.value = countryFilters.value.length - 1;
    maxFilterId.value += 1;
  }

  function onFilterSelect(index: number): void {
    activeFilterIndex.value = index;
  }

  function onFilterExpandToggle(index: number): void {
    if (countryFilters.value[index]) {
      countryFilters.value.splice(index, 1, {
        ...countryFilters.value[index],
        expanded: !countryFilters.value[index].expanded,
      });
    }
  }

  function onFilterRemove(index: number): void {
    const revertActive = activeFilterIndex.value === index;

    const activeFilter = countryFilters.value[index];

    if (activeFilter) {
      let activeFilterDiff = 0;

      if (activeFilter.id) {
        countryFilters.value.splice(index, 1, {
          ...activeFilter,
          _destroy: 1,
        });
      } else {
        countryFilters.value.splice(index, 1);
        activeFilterDiff = 1;
      }

      if (revertActive) {
        activeFilterIndex.value = 0;
      } else if (activeFilterIndex.value > index) {
        activeFilterIndex.value -= activeFilterDiff;
      }
    }
  }

  function recalculateFilters(): void {
    activeFilterIndex.value = 0;

    if (commissionFilters.value) {
      countryFilters.value = sortByProperty(commissionFilters.value.map((item) => {
        const isAllCountries = item.name === 'filters.filter_all_countries';
        maxFilterId.value = item.id && item.id > maxFilterId.value ? item.id : maxFilterId.value;

        return {
          ...item,
          label: isAllCountries ? undefined : item.name,
          emptyLabel: '',
          emptyLabelId: item.id,
          value: item.value,
          expanded: false,
          isAllCountries,
          condition_items_attributes: item.condition_items_attributes,
          condition_steps_attributes: item.condition_steps_attributes,
          _destroy: 0,
        } as ICommissionCountryFilterItem;
      }), 'isAllCountries');
    } else {
      countryFilters.value = [{
        label: undefined,
        emptyLabel: '',
        emptyLabelId: maxFilterId.value + 1,
        value: [],
        expanded: false,
        isAllCountries: true,
        condition_items_attributes: [],
        condition_steps_attributes: [],
        _destroy: 0,
      } as ICommissionCountryFilterItem];
    }
  }

  watchImmediate(commissionFilters, recalculateFilters, { deep: true });
  watch(transactionType, () => {
    countryFilters.value = countryFilters.value.map((item) => ({
      ...item,
      condition_items_attributes: [],
      condition_steps_attributes: [],
    }));
  });
  watch(currentSecondGroupVersion, (newValue, oldValue) => {
    if (oldValue && newValue) {
      countryFilters.value = countryFilters.value.map((item) => ({
        ...item,
        condition_steps_attributes: item.condition_steps_attributes.filter((step) => step.type_in_part_condition !== secondGroupStepTransactionType.value),
      }));
    }
  });

  return {
    activeFilterIndex,
    countryFilters,
    onFilterAdd,
    onFilterSelect,
    onFilterExpandToggle,
    onFilterRemove,
  };
}
